import { useEffect, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Plus, Trash2, ChevronUp, ChevronDown, ChevronRight, X } from 'lucide-react';
import {
  api, parseApiError, COMP_STAGE_TYPES,
  type CreateCompetitionInput, type UpdateCompetitionInput, type Competition, type CompStageType,
} from '../lib/api';
import { useWorkspace } from '../lib/workspace';
import { cn } from '../lib/cn';
import { useSectionHighlightContext } from '../lib/sectionHighlight';
import { PRESETS, type ModeConfig, type SeriesFormat } from '../lib/modes';
import { StagePipelinePanel, STAGE_TYPE_LABELS, StageConfigFields, STANDINGS_TYPE_STAGE_TYPES } from './StagePipelinePanel';
import { ClanRollupPanel } from './ClanRollupPanel';
import { SeriesFormatEditor } from './SeriesFormatEditor';
import { GameModesEditor } from './GameModesEditor';
import { CollapsibleSection } from './CollapsibleSection';
import { HelpText } from './HelpText';

// ── Stage composition (T2 — stage-first creation) ───────────────────────────
// Local-only ordered builder: the comp doesn't exist yet at create time, so
// this is plain useState, not the STAGE#-row mutations StagePipelinePanel
// drives post-creation. Array order === ordinal; POST /orgs/:orgId/competitions
// (or /competitions) validates + writes every row in the SAME transaction as
// the comp itself — see shared/compStages.ts buildStageRow/validateStageList.
// `uid` is a LOCAL-ONLY client id (never sent to the API — stripped in
// CompetitionCreateForm's submit below) used purely as a stable React key: an
// array-index key would have reorder (moveStage swaps positions, not
// identity) silently drag a stage's local UI state (its expand/collapse
// below) onto whichever OTHER stage ends up at that position.
type ComposedStage = { uid: string; type: CompStageType; config: Record<string, unknown> };

// T4: every NEW stage the composer adds defaults standingsType 'SCORE' for
// the types that carry it at all — matches STANDINGS_TYPE_STAGE_TYPES
// exported from StagePipelinePanel.tsx (mirrors shared/compStages.ts's
// STANDINGS_TYPE_ALLOWED; ELIMINATION_SINGLE/ELIMINATION_DOUBLE would 400 if
// the key rode along on their config at all).

// ── StageComposerRow — one composed-stage row, its own chevron state ───────
// Extracted so the list `.map()` in StageComposer below never puts a hook
// (the expand/collapse toggle) in a loop — same precedent StagePipelinePanel's
// StagePipelineCard already sets for the post-creation editor.
function StageComposerRow({
  stage, index, isFirst, isLast, isOnly, modes, defaultSeriesFormat, onMove, onRemove, onConfigChange,
}: {
  stage: ComposedStage;
  index: number;
  isFirst: boolean;
  isLast: boolean;
  isOnly: boolean;
  modes: ModeConfig[];
  defaultSeriesFormat: SeriesFormat;
  onMove: (dir: -1 | 1) => void;
  onRemove: () => void;
  onConfigChange: (config: Record<string, unknown>) => void;
}) {
  // Expandable card (T3 — chevron, matching CollapsibleSection's house
  // idiom). Default COLLAPSED (Lisa's QA), same rule StagePipelinePanel's
  // post-creation cards use — EXCEPT the only composed stage so far, which
  // stays open since there's nothing yet to declutter.
  const [expanded, setExpanded] = useState(isOnly);

  return (
    // Distinct per-stage card, same elevation treatment as StagePipelinePanel's
    // post-creation cards — bg-white/[0.07] (one step above the composer's own
    // bg-surface-900 section bg) + the house shadow idiom, no box border;
    // vertical spacing + the quiet connector rail between rows come from
    // StageComposer's list below. The 2px accent edge is a pointer to
    // whichever row is currently expanded, never a static border.
    <div className={cn('rounded-lg bg-white/[0.07] p-3 shadow-elev1', expanded && 'border-l-2 border-accent-500')}>
      <div className="flex items-center justify-between gap-2">
        <button type="button" onClick={() => setExpanded(e => !e)} className="flex min-w-0 flex-1 items-center gap-2 text-left">
          {expanded
            ? <ChevronDown className="h-3.5 w-3.5 shrink-0 text-gray-500" />
            : <ChevronRight className="h-3.5 w-3.5 shrink-0 text-gray-500" />}
          <span className={cn(
            'flex h-7 w-7 shrink-0 items-center justify-center rounded-md font-display text-[11px] font-black',
            expanded ? 'bg-accent-500/10 text-accent-500' : 'bg-white/[0.08] text-ink-secondary'
          )}>
            {index + 1}
          </span>
          <span className="text-sm text-white">{STAGE_TYPE_LABELS[stage.type]}</span>
        </button>
        <div className="flex shrink-0 items-center gap-1">
          <button type="button" title="Move up" disabled={isFirst} onClick={() => onMove(-1)}
            className="rounded p-1 text-gray-500 transition hover:text-white disabled:opacity-30">
            <ChevronUp className="h-3.5 w-3.5" />
          </button>
          <button type="button" title="Move down" disabled={isLast} onClick={() => onMove(1)}
            className="rounded p-1 text-gray-500 transition hover:text-white disabled:opacity-30">
            <ChevronDown className="h-3.5 w-3.5" />
          </button>
          <button type="button" title="Remove stage" onClick={onRemove}
            className="rounded p-1 text-gray-500 transition hover:text-red-400">
            <X className="h-3.5 w-3.5" />
          </button>
        </div>
      </div>
      {expanded && (
        // Body separated from the header row by a real gap (mt-3), same
        // breathing room StagePipelinePanel's cards give their own body.
        <div className="mt-3">
          {/* numTeams is intentionally omitted (undefined) — the comp doesn't
              exist yet at create time, so there are no enrolled teams for
              DIVISIONS' Suggest button to size a plan against; StageConfigFields
              shows the muted "available once teams enroll" note instead. */}
          <StageConfigFields
            type={stage.type}
            config={stage.config}
            modes={modes}
            defaultSeriesFormat={defaultSeriesFormat}
            onChange={onConfigChange}
          />
        </div>
      )}
    </div>
  );
}

function StageComposer({ stages, onChange, modes, defaultSeriesFormat }: {
  stages: ComposedStage[];
  onChange: (s: ComposedStage[]) => void;
  modes: ModeConfig[];
  defaultSeriesFormat: SeriesFormat;
}) {
  const [pickerOpen, setPickerOpen] = useState(false);

  // Stamp each type's sensible defaults immediately on add — same precedent
  // SEEDING already set (rounds: 5) — so the composer's fields never start
  // on an empty/undefined value the admin has to notice and fill in. Every
  // new stage also seeds the comp's CURRENT series format as its own
  // starting point (T3) — the Ruleset preset stays comp-level as the source
  // of mode definitions + the default series a new stage card starts from.
  const DEFAULT_STAGE_CONFIG: Partial<Record<CompStageType, Record<string, unknown>>> = {
    SEEDING: { rounds: 5 },
    SWISS: { winThreshold: 3, lossThreshold: 3 },
    DIVISIONS: { numGroups: 2, advancePerGroup: 4, gamesPerWeek: 1 },
  };
  const addStage = (type: CompStageType) => {
    onChange([...stages, { uid: crypto.randomUUID(), type, config: {
      ...(DEFAULT_STAGE_CONFIG[type] ?? {}),
      seriesFormat: defaultSeriesFormat,
      // T4: default standingsType 'SCORE' — matches the select's own
      // "default" option — but only for types that carry the key at all;
      // ELIMINATION_SINGLE/ELIMINATION_DOUBLE would 400 if it rode along.
      ...(STANDINGS_TYPE_STAGE_TYPES.includes(type) ? { standingsType: 'SCORE' as const } : {}),
    } }]);
    setPickerOpen(false);
  };
  const removeStage = (i: number) => onChange(stages.filter((_, j) => j !== i));
  const moveStage = (i: number, dir: -1 | 1) => {
    const j = i + dir;
    if (j < 0 || j >= stages.length) return;
    const next = [...stages];
    [next[i], next[j]] = [next[j], next[i]];
    onChange(next);
  };
  const updateStageConfig = (i: number, config: Record<string, unknown>) => {
    const next = [...stages];
    next[i] = { ...next[i], config };
    onChange(next);
  };

  return (
    // !mt-8 (Lisa's QA — "everything melts together"): the Stages composer is
    // the first REQUIRED heavy section after the basics fields above; the
    // important-modifier is needed because the parent form's space-y-3 (a
    // higher-specificity `> :not([hidden]) ~ :not([hidden])` selector) would
    // otherwise win over a plain mt-8 on this same element — same override
    // idiom CollapsibleSection.tsx's `!p-4` already uses on its own Card.
    <div className="!mt-8 space-y-2 rounded-lg border border-forge-500/20 bg-surface-900 p-4">
      <div>
        <p className="text-xs font-semibold uppercase tracking-wider text-forge-400">Stages</p>
        <p className="text-[11px] text-gray-500">
          Compose an ordered pipeline — each stage carries its own settings (seeding might run Bo3, the
          round robin proper Bo5).
        </p>
        <HelpText>
          <p className="mt-1 text-[11px] text-gray-500">
            Stages are composed here, before the competition exists — add as many as the format needs, in
            order. Once created, each stage's settings stay editable from the Stage Pipeline panel while
            it's still pending, and lock the moment it starts.
          </p>
        </HelpText>
      </div>

      {stages.length > 0 && (
        // Connector line replaces the plain space-y-3 gap — same rail idiom
        // StagePipelinePanel's post-creation list uses — so a multi-stage
        // pipeline reads as one composed sequence, not a stack of loose cards.
        <div>
          {stages.map((s, i) => (
            <div key={s.uid}>
              <StageComposerRow
                stage={s}
                index={i}
                isFirst={i === 0}
                isLast={i === stages.length - 1}
                isOnly={stages.length === 1}
                modes={modes}
                defaultSeriesFormat={defaultSeriesFormat}
                onMove={(dir) => moveStage(i, dir)}
                onRemove={() => removeStage(i)}
                onConfigChange={(cfg) => updateStageConfig(i, cfg)}
              />
              {i < stages.length - 1 && <div className="ml-12 h-3 w-px bg-white/[0.08]" />}
            </div>
          ))}
        </div>
      )}

      {/* "+ Add stage" — moved to the BOTTOM of the list (Lisa's QA: same fix
          as StagePipelinePanel's post-creation panel), where the new stage
          card actually lands. The type picker opens in place of this row. */}
      <div className={stages.length > 0 ? 'mt-1' : undefined}>
        {!pickerOpen ? (
          <button
            type="button"
            onClick={() => setPickerOpen(true)}
            className="flex w-full items-center gap-1.5 rounded-md px-2 py-1.5 text-xs text-gray-500 transition hover:bg-white/[0.04] hover:text-white"
          >
            <Plus className="h-3.5 w-3.5" /> Add stage
          </button>
        ) : (
          <div className="rounded-lg bg-white/[0.04] p-3">
            <p className="mb-2 text-xs font-medium uppercase tracking-wide text-gray-500">Choose a stage type</p>
            <div className="flex flex-wrap gap-1.5">
              {COMP_STAGE_TYPES.map(type => (
                <button key={type} type="button" onClick={() => addStage(type)}
                  className="rounded-md bg-white/[0.06] px-2.5 py-1 text-xs text-white transition hover:bg-white/[0.1]">
                  {STAGE_TYPE_LABELS[type]}
                </button>
              ))}
            </div>
            <button type="button" onClick={() => setPickerOpen(false)} className="mt-2 text-xs text-gray-500 hover:text-white">
              Cancel
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

function ErrorMsg({ msg }: { msg: string }) {
  return msg ? <p className="mt-2 text-xs text-red-400">{msg}</p> : null;
}

// ── Delete competition (danger zone, edit mode only) ────────────────────────
// SOFT delete — mirrors ClanWorkbench.tsx's DeleteClanButton: a single native
// confirm() (no separate two-step UI in this codebase's destructive idiom),
// hidden immediately, kept 30 days server-side before permanent purge. Child
// rows (stages, rules, memberships, enrolled clans' entries) are left in
// place — see the backend comment on DELETE /competitions/:compId.
function DeleteCompetitionButton({ compId, name, isLive, onDeleted }: {
  compId: string; name: string; isLive: boolean; onDeleted: () => void;
}) {
  const [error, setError] = useState('');
  const mut = useMutation({
    mutationFn: (confirm: boolean) => api.deleteCompetition(compId, { confirm }),
    onSuccess: () => onDeleted(),
    onError: (e: Error) => setError(parseApiError(e).message),
  });

  const handleClick = () => {
    const liveWarning = isLive
      ? `"${name}" is LIVE right now — teams are actively competing in it. Deleting it hides it immediately for everyone. `
      : '';
    const confirmed = window.confirm(
      `${liveWarning}Delete the competition "${name}"? It disappears from every list right away and is kept for 30 days before permanent removal — ask a founder to recover it within that window if this was a mistake.`
    );
    if (!confirmed) return;
    mut.mutate(isLive);
  };

  return (
    <div className="mt-2 border-t border-red-500/15 pt-3">
      <button
        type="button"
        onClick={handleClick}
        disabled={mut.isPending}
        className="inline-flex items-center gap-1.5 rounded-lg border border-red-500/40 bg-red-900/20 px-3 py-1.5 text-xs font-semibold uppercase tracking-wider text-red-300 hover:bg-red-900/40 disabled:opacity-50"
      >
        <Trash2 className="h-3.5 w-3.5" /> {mut.isPending ? 'Deleting…' : 'Delete competition'}
      </button>
      {error && <p className="mt-1 text-xs text-red-400">{error}</p>}
    </div>
  );
}

function buildSeriesFormat(modes: ModeConfig[], length: number): SeriesFormat {
  const order: string[] = [];
  for (let i = 0; i < length; i++) order.push(modes[i % modes.length].key);
  return { length, modeOrder: order, winsNeeded: Math.ceil(length / 2) };
}

export function CompetitionCreateForm({
  onDone,
  orgId,
  advanced = true,
  defaultOpen,
  onCancel,
  editComp,
  openTrigger,
}: {
  /** Called once the submit succeeds. On a fresh CREATE (never edit), the
   * newly created competition's { id, name } rides along so callers that
   * need to pin the just-created comp as "active" (e.g. the go-live
   * checklist's jump-to-create) don't have to guess it back out of a
   * re-fetched list. */
  onDone: (created?: { id: string; name: string }) => void;
  orgId?: string;
  advanced?: boolean;
  defaultOpen?: boolean;
  onCancel?: () => void;
  // Dual-mode: when present, the form edits this existing (PENDING-only)
  // competition instead of creating a new one. Create path is untouched when
  // this prop is absent — every `!isEdit` guard below is a no-op for it.
  editComp?: Competition;
  /** Bump this (any changing number) to force the collapsed create-mode form
   * open — the go-live checklist's "Competition created"/"Stages composed"
   * jump uses this so clicking the checklist item doesn't land on a
   * still-collapsed "+ New Competition" button. No-op in edit mode (already
   * always open). Guarded to skip the initial mount — only a real CHANGE
   * should force it open, not the prop merely being present. */
  openTrigger?: number;
}) {
  const isEdit = !!editComp;
  const [open, setOpen] = useState(isEdit ? true : (defaultOpen ?? false));
  const highlight = useSectionHighlightContext();
  const openTriggerRef = useRef(openTrigger);
  useEffect(() => {
    if (openTrigger !== undefined && openTrigger !== openTriggerRef.current) {
      openTriggerRef.current = openTrigger;
      setOpen(true);
    }
  }, [openTrigger]);
  const [presetId, setPresetId] = useState(PRESETS[0].id);
  // W3 soft-stamp: when no explicit org prop is given, fall back to the active
  // workspace org so the bare /competitions create still gets stamped.
  const { activeOrgId, activeOrg } = useWorkspace();
  const [form, setForm] = useState<CreateCompetitionInput>(() => editComp
    ? {
        name: editComp.name,
        teamSize: editComp.teamSize,
        format: editComp.format,
        modes: editComp.modes ?? PRESETS[0].modes,
        seriesFormat: editComp.seriesFormat ?? buildSeriesFormat(editComp.modes ?? PRESETS[0].modes, 5),
      }
    : {
        // P2-b (guided-wizard Phase 2, Lisa 2026-07-16): default team size is
        // 6v6 (was 4) — still fully overridable via the select below.
        name: '', teamSize: 6, startDate: '', endDate: '',
        modes: PRESETS[0].modes, seriesFormat: PRESETS[0].series,
        standingsType: 'SCORE',
      });
  const [error, setError] = useState('');
  // Stage-first creation (T2) — create mode only; the comp doesn't exist yet
  // so this is local state until submit. Edit mode's structure lives entirely
  // in StagePipelinePanel (mounted below, under the fields) instead.
  const [stages, setStages] = useState<ComposedStage[]>([]);

  // T1 sign-up window (Lisa, 2026-07-16) — create mode only, set-at-creation
  // ONLY (no PATCH/edit path exists — see shared/compGates.ts on the backend).
  // Local <input type="datetime-local"> strings (browser-local wall-clock,
  // no timezone suffix) — converted to unix-ms instants on submit below via
  // `new Date(local).getTime()`, which JS already interprets in the browser's
  // local timezone, so this needs no manual UTC math. Both optional; either,
  // both, or neither may be set.
  const [signUpOpensAt, setSignUpOpensAt] = useState('');
  const [signUpClosesAt, setSignUpClosesAt] = useState('');
  const signUpWindowInverted = !!signUpOpensAt && !!signUpClosesAt && new Date(signUpOpensAt).getTime() >= new Date(signUpClosesAt).getTime();

  const handlePreset = (id: string) => {
    setPresetId(id);
    const p = PRESETS.find(pr => pr.id === id);
    if (p) setForm(f => ({ ...f, modes: p.modes, seriesFormat: p.series }));
  };

  const updateModes = (modes: ModeConfig[]) => {
    setPresetId('custom');
    const series = form.seriesFormat ?? buildSeriesFormat(modes, 5);
    const fixedOrder = series.modeOrder.map((k, i) => modes.find(m => m.key === k) ? k : modes[i % modes.length]?.key ?? '');
    setForm(f => ({ ...f, modes, seriesFormat: { ...series, modeOrder: fixedOrder } }));
  };

  // Progressive lock: teamSize locks the moment a comp goes LIVE (clans are
  // enrolled at that size), even before the round-robin/mid-stage starts.
  // Everything else (name/format/modes/seriesFormat) stays editable until the
  // mid-stage actually begins (stage past SEEDING) or the comp closes.
  const teamSizeLocked = editComp?.status === 'LIVE';

  // Rules/terms text — edit-mode only. Prefilled from GET terms-acceptance
  // (the same row PATCH .../competitions/:compId writes/bumps) rather than a
  // prop, since the Competition object the list/workspace queries return
  // doesn't carry the rules text — this is the one place that needs it, so a
  // dedicated fetch keeps the shared Competition type from growing a field
  // almost nothing else reads.
  const queryClient = useQueryClient();
  const rulesQuery = useQuery({
    queryKey: ['compTermsAcceptance', editComp?.id],
    queryFn: () => api.getCompTermsAcceptance(editComp!.id),
    enabled: isEdit,
  });
  const [rulesText, setRulesText] = useState('');
  const seededRulesRef = useRef(false);
  useEffect(() => {
    if (rulesQuery.data && !seededRulesRef.current) {
      setRulesText(rulesQuery.data.rules);
      seededRulesRef.current = true;
    }
  }, [rulesQuery.data]);
  const originalRules = rulesQuery.data?.rules ?? '';

  // T3 (per-stage config reshape): does this (existing) comp already have a
  // composed stage pipeline? Shares StagePipelinePanel's OWN queryKey
  // (['compStages', compId]) so React Query dedupes the two — one network
  // request either way — and drives whether the comp-level Series Format
  // block below hides in favour of each stage's own (StagePipelinePanel,
  // mounted further down).
  const compStagesQuery = useQuery({
    queryKey: ['compStages', editComp?.id],
    queryFn: () => api.listCompStages(editComp!.id),
    enabled: isEdit,
  });
  const compHasStages = (compStagesQuery.data?.length ?? 0) > 0;

  const mut = useMutation<unknown, Error, CreateCompetitionInput>({
    mutationFn: (data: CreateCompetitionInput) => {
      if (editComp) {
        // PATCH only accepts this fixed field set — the backend hard-rejects
        // anything else (orgId/status/stage are never editable here). teamSize
        // is omitted entirely once locked — the backend rejects the WHOLE
        // request (team_size_locked) if teamSize is present at all while LIVE,
        // even when its value is unchanged. `rules` only rides along when it
        // actually changed — sending it unconditionally would bump
        // rulesVersion (the audit-trail counter — acceptance itself is
        // durable and never forces re-accept) on every unrelated save.
        const patch: UpdateCompetitionInput = {
          name: data.name, format: data.format,
          modes: data.modes, seriesFormat: data.seriesFormat,
          ...(teamSizeLocked ? {} : { teamSize: data.teamSize }),
          ...(rulesText !== originalRules ? { rules: rulesText } : {}),
        };
        return api.updateCompetition(editComp.id, patch);
      }
      // Stage-first creation (T2): a composed pipeline rides along and the
      // backend derives/overrides format + seedingRoundsTotal from it —
      // server is authoritative regardless of what `data.format` still holds.
      // `uid` is stripped — it's a local React-key-only field (see
      // ComposedStage above); the API's stage shape never had it.
      const withStages: CreateCompetitionInput = stages.length > 0
        ? { ...data, stages: stages.map(({ type, config }) => ({ type, config })) }
        : data;
      // T1 sign-up window — create mode only, converted from the local
      // datetime-local strings to unix-ms instants here at submit time.
      const withSignUpWindow: CreateCompetitionInput = {
        ...withStages,
        ...(signUpOpensAt ? { signUpOpensAt: new Date(signUpOpensAt).getTime() } : {}),
        ...(signUpClosesAt ? { signUpClosesAt: new Date(signUpClosesAt).getTime() } : {}),
      };
      return orgId
        ? api.createOrgCompetition(orgId, withSignUpWindow)
        : api.createCompetition({ ...withSignUpWindow, orgId: activeOrgId ?? undefined });
    },
    onSuccess: (data) => {
      if (!isEdit) {
        setOpen(false);
        setForm({ name: '', teamSize: 6, startDate: '', endDate: '', modes: PRESETS[0].modes, seriesFormat: PRESETS[0].series, standingsType: 'SCORE' });
        setPresetId(PRESETS[0].id);
        setStages([]);
        setSignUpOpensAt('');
        setSignUpClosesAt('');
        onDone(data as { id: string; name: string });
        return;
      } else if (rulesText !== originalRules) {
        queryClient.invalidateQueries({ queryKey: ['compTermsAcceptance', editComp?.id] });
      }
      onDone();
    },
    onError: (e: Error) => setError(parseApiError(e).message),
  });

  if (!open)
    return (
      <button onClick={() => setOpen(true)} className="btn-primary flex items-center gap-1.5 text-sm">
        <Plus className="h-4 w-4" /> New Competition
      </button>
    );

  const currentModes = form.modes ?? [];
  const currentSeries = form.seriesFormat ?? buildSeriesFormat(currentModes, 5);
  // All built-in formats are standard and available to every org.
  // activeOrg?.enabledFormats remains available for any future bespoke/extra formats.
  const allowedFormats = new Set<string>(['ROUND_ROBIN', 'ELIMINATION_SINGLE', 'ELIMINATION_DOUBLE', 'SWISS', 'GROUP_STAGE', ...(activeOrg?.enabledFormats ?? [])]);

  return (
    <div
      ref={highlight?.registerSection('comp-create')}
      className={cn(
        'mt-3 rounded-xl border border-violet-500/30 bg-surface-950 p-4 space-y-3',
        highlight?.isHighlighted('comp-create') && 'section-pulse',
      )}
    >
      <p className="text-xs font-semibold uppercase tracking-wider text-violet-400">
        {isEdit ? `Edit Competition — ${editComp.name}` : `New Competition${orgId ? ' (org-scoped)' : ''}`}
      </p>
      {isEdit && (
        <p className="rounded-lg border border-amber-500/30 bg-amber-500/5 px-3 py-2 text-xs text-amber-200">
          Settings stay editable until the round robin starts — then the setup locks permanently.
          Team size locks when the competition goes live.
        </p>
      )}
      <input type="text" placeholder="Competition name" value={form.name}
        onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
        className="input w-full" />
      {/* Phase 4b (Lisa's request): comp dates + the registration window grouped
          into one collapsible pill, same CollapsibleSection pattern/styling as
          the Game Modes pill below — pure regroup, no field behaviour change.
          Create mode only (both grids were already !isEdit-gated). */}
      {!isEdit && (
        <CollapsibleSection
          title="Dates & Registration Window"
          hint="Competition start/end and when clans may register — both optional, fixed at creation."
        >
          <div className="space-y-3 rounded-lg border border-forge-500/20 bg-surface-900 p-4">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="label">Start date</label>
                <input type="date" value={form.startDate} onChange={e => setForm(f => ({ ...f, startDate: e.target.value }))} className="input w-full [color-scheme:dark]" />
              </div>
              <div>
                <label className="label">End date</label>
                <input type="date" value={form.endDate} onChange={e => setForm(f => ({ ...f, endDate: e.target.value }))} className="input w-full [color-scheme:dark]" />
              </div>
            </div>
            {/* T1 sign-up window (Lisa, 2026-07-16) — create mode only, optional.
                Distinct from Start/End date above: this gates WHEN CLANS MAY
                REGISTER, not when the competition itself runs. Start date remains
                the ultimate backstop (registration always closes there regardless
                of this window) — see shared/compGates.ts. */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="label">Registration opens</label>
                <input
                  type="datetime-local"
                  value={signUpOpensAt}
                  onChange={e => setSignUpOpensAt(e.target.value)}
                  className="input w-full [color-scheme:dark]"
                />
              </div>
              <div>
                <label className="label">Registration closes</label>
                <input
                  type="datetime-local"
                  value={signUpClosesAt}
                  onChange={e => setSignUpClosesAt(e.target.value)}
                  className="input w-full [color-scheme:dark]"
                />
              </div>
              <p className="col-span-2 text-[11px] text-gray-500">
                Optional — narrows exactly when clans may register. Leave blank to allow registration any time
                until the start date above (the existing behaviour, unchanged).
              </p>
              {signUpWindowInverted && (
                <p className="col-span-2 text-xs text-red-400">Registration opens must be before it closes.</p>
              )}
            </div>
          </div>
        </CollapsibleSection>
      )}
      {/* T4 reshape (Lisa's QA of #219): comp-level BASICS are now ONLY
          name/dates (above), team size, and ruleset — format, standings
          type, and series format are per-STAGE settings, full stop, and
          moved into the (now mandatory) Stages composer below. The
          coexistence boundary: CREATE is always stage-driven, so none of
          those three ever appear here in create mode. EDIT mode keeps
          showing them as LEGACY comp-level controls, but ONLY while the comp
          has no stages of its own (`!compHasStages` below, and again on the
          Series Format block further down) — the instant a comp gains a
          stage pipeline (new comps always have one; an old comp can grow one
          via the Stage Pipeline panel), these legacy controls fold away
          behind a single pointer note. */}
      <div className={advanced ? 'grid grid-cols-2 gap-3 sm:grid-cols-4' : 'grid grid-cols-2 gap-3'}>
        <div>
          <label className="label">Team size</label>
          <select
            value={form.teamSize}
            disabled={teamSizeLocked}
            onChange={e => setForm(f => ({ ...f, teamSize: Number(e.target.value) }))}
            className="input w-full disabled:cursor-not-allowed disabled:opacity-50"
          >
            {[2, 3, 4, 5, 6, 8].map(n => <option key={n} value={n}>{n}v{n}{n === 6 ? ' — default' : ''}</option>)}
          </select>
          {teamSizeLocked && <p className="mt-1 text-[11px] text-gray-500">Locked at go-live.</p>}
        </div>
        {isEdit && !compHasStages && (
          <div>
            <label className="label">Format</label>
            <select value={form.format ?? 'ROUND_ROBIN'} onChange={e => setForm(f => ({ ...f, format: e.target.value as CreateCompetitionInput['format'] }))} className="input w-full">
              <option value="ROUND_ROBIN">Round robin — default</option>
              <option value="ELIMINATION_SINGLE">Single elimination</option>
              <option value="ELIMINATION_DOUBLE">Double elimination</option>
              {allowedFormats.has('SWISS') && <option value="SWISS">Swiss system</option>}
              {allowedFormats.has('GROUP_STAGE') && <option value="GROUP_STAGE">Groups → Single elimination</option>}
            </select>
          </div>
        )}
        {advanced && (
          <>
            {isEdit && !compHasStages && (
              <div>
                <label className="text-[10px] font-medium uppercase tracking-wider text-gray-500">Standings type</label>
                <select value={form.standingsType ?? 'SCORE'} onChange={e => setForm(f => ({ ...f, standingsType: e.target.value as 'WIN_LOSS' | 'SCORE' }))} className="input w-full text-xs opacity-80">
                  <option value="SCORE">Score-based — default</option>
                  <option value="WIN_LOSS">Win / Loss</option>
                </select>
              </div>
            )}
            <div>
              <label className="label">Starting template</label>
              <select value={presetId} onChange={e => handlePreset(e.target.value)} className="input w-full">
                {PRESETS.map(p => <option key={p.id} value={p.id}>{p.label}</option>)}
                {presetId === 'custom' && <option value="custom">Custom</option>}
              </select>
              <p className="mt-1 text-[11px] text-gray-500">Seeds the game modes and series format — fully editable after. Each stage's own Series Format is what its matches actually play.</p>
            </div>
          </>
        )}
      </div>

      {/* SWISS/GROUP_STAGE per-comp mini panels — same legacy-only
          coexistence rule as Format/Standings above. A composed SWISS/
          DIVISIONS stage carries its own winThreshold/lossThreshold/
          numGroups/advancePerGroup right on its own card (StageConfigFields)
          instead, so these never show once the comp has stages. */}
      {isEdit && !compHasStages && form.format === 'SWISS' && (
        <div className="grid grid-cols-2 gap-3 rounded-lg border border-violet-500/20 bg-surface-900 p-3">
          <div>
            <label className="label">Wins to advance (IN)</label>
            <input type="number" min={1} value={form.swissWinThreshold ?? 3}
              onChange={e => setForm(f => ({ ...f, swissWinThreshold: Number(e.target.value) }))}
              className="input w-full" />
          </div>
          <div>
            <label className="label">Losses to eliminate (OUT)</label>
            <input type="number" min={1} value={form.swissLossThreshold ?? 3}
              onChange={e => setForm(f => ({ ...f, swissLossThreshold: Number(e.target.value) }))}
              className="input w-full" />
          </div>
          <p className="col-span-2 text-[11px] text-gray-500">
            Record-based Swiss: teams pair by matching record each round, never replay an
            opponent, and are seeded by Buchholz. Default 3 / 3 is the standard 16-team Major stage.
          </p>
        </div>
      )}

      {isEdit && !compHasStages && form.format === 'GROUP_STAGE' && (
        <div className="grid grid-cols-2 gap-3 rounded-lg border border-violet-500/20 bg-surface-900 p-3">
          <div>
            <label className="label">Number of groups</label>
            <input type="number" min={1} value={form.groupCount ?? 2}
              onChange={e => setForm(f => ({ ...f, groupCount: Number(e.target.value) }))}
              className="input w-full" />
          </div>
          <div>
            <label className="label">Advance per group</label>
            <input type="number" min={1} value={form.advancePerGroup ?? 4}
              onChange={e => setForm(f => ({ ...f, advancePerGroup: Number(e.target.value) }))}
              className="input w-full" />
          </div>
          <p className="col-span-2 text-[11px] text-gray-500">
            Each group plays a round-robin; the top teams advance to a single-elimination playoff.
            e.g. 10 teams · 2 groups · top 4 = an 8-team bracket. Works for any team count.
          </p>
        </div>
      )}

      {/* Stages composer — REQUIRED in create mode (T4): every competition
          runs as a stage pipeline, full stop. Format/standings type/series
          format are ALL stage-owned settings composed here (each stage card
          carries its own type-specific knobs + standings type + series
          format) — there is no comp-level fallback in create mode any more. */}
      {!isEdit && (
        <div
          ref={highlight?.registerSection('comp-stages')}
          className={cn(highlight?.isHighlighted('comp-stages') && 'section-pulse rounded-lg')}
        >
          <StageComposer stages={stages} onChange={setStages} modes={currentModes} defaultSeriesFormat={currentSeries} />
          {stages.length === 0 && (
            <p className="px-1 text-[11px] text-gray-500">
              Add at least one stage — every competition runs as a stage pipeline.
            </p>
          )}
        </div>
      )}

      {/* Game-mode DEFINITIONS stay comp-level (defined once — every stage's
          series format below picks its mode order from this same list) but
          move into a collapsed section so the basics above stay clean. */}
      {advanced && (
        <CollapsibleSection
          title="Game Modes"
          hint="Defines the game's modes + seeding points once — every stage's series format picks from these"
          className="!mt-8"
        >
          <div className="space-y-4 rounded-lg border border-forge-500/20 bg-surface-900 p-4">
            <div>
              <p className="text-[11px] text-gray-500 mb-2">
                Multiplier = points a team banks per unit won in this mode ({'"'}unit{'"'} = a point or round, per the mode).
                A team{"'"}s mode score × multiplier feeds the seeding standings shown on the Seeding page.
              </p>
              <GameModesEditor modes={currentModes} onChange={updateModes} />
              {!isEdit && stages.length === 0 && (
                <div className="mt-3 flex items-center gap-3">
                  <label className="label shrink-0">Seeding rounds</label>
                  <input type="number" min={0} value={form.seedingRoundsTotal ?? 5}
                    onChange={e => setForm(f => ({ ...f, seedingRoundsTotal: Number(e.target.value) }))}
                    className="input w-20 text-xs" />
                  <span className="text-[11px] text-gray-500">Rounds each team plays in the seeding stage. 0 = no seeding stage.</span>
                </div>
              )}
            </div>
          </div>
        </CollapsibleSection>
      )}

      {/* Comp-level Series Format — this is the LEGACY single-format block,
          same coexistence rule as Format/Standings above: EDIT mode only,
          and only while the comp has no stages of its own. Once a comp gains
          a stage pipeline (every new comp does, immediately — CREATE never
          shows this block at all), each stage carries its OWN series format
          (StageComposer at create-time / StagePipelinePanel below at
          edit-time) and all three legacy comp-level controls fold away
          behind the single pointer note below. */}
      {advanced && isEdit && !compHasStages && (
        <div className="space-y-2 rounded-lg border border-forge-500/20 bg-surface-900 p-4">
          <p className="text-xs font-semibold uppercase tracking-wider text-forge-400 mb-2">Series Format</p>
          {currentModes.length > 0 ? (
            <SeriesFormatEditor modes={currentModes} series={currentSeries}
              onChange={s => { setPresetId('custom'); setForm(f => ({ ...f, seriesFormat: s })); }} />
          ) : (
            <p className="text-xs text-gray-500">Add at least one game mode first.</p>
          )}
        </div>
      )}
      {advanced && isEdit && compHasStages && (
        <p className="px-1 text-[11px] text-gray-500">
          This competition's stages carry their own format, standings type, and series format now — configure each one in the Stage Pipeline below.
        </p>
      )}

      {isEdit && (
        <div
          ref={highlight?.registerSection('comp-rules')}
          className={cn(
            'space-y-2 rounded-lg border border-forge-500/20 bg-surface-900 p-4',
            highlight?.isHighlighted('comp-rules') && 'section-pulse',
          )}
        >
          <label className="label">Rules &amp; terms (shown to teams when they accept)</label>
          <textarea
            value={rulesText}
            onChange={e => setRulesText(e.target.value)}
            rows={8}
            placeholder="Paste this competition's rules/terms text — a clan leader sees this before they accept and self-register their team."
            className="input w-full resize-y font-mono text-xs leading-relaxed"
          />
          <p className="text-[11px] text-gray-500">
            Every competition is seeded with default rules text — this box holds a customised replacement, if
            you've written one. Version-stamped for the audit trail. Tip: include a "rules are subject to
            change by the organiser" clause — accepted teams remain accepted when rules are updated.
          </p>
        </div>
      )}

      {/* Stage Pipeline (T2) — the pencil is the ONE home for comp structure
          now; this is StagePipelinePanel's only mount (CompPane/TournamentSetup
          no longer renders it standalone). Progressive per-stage lock is
          backend-enforced (shared/compStages.ts) — this component just wires
          the existing stage routes from #213 into the edit form itself. */}
      {isEdit && (
        <div
          ref={highlight?.registerSection('comp-stages')}
          className={cn(highlight?.isHighlighted('comp-stages') && 'section-pulse rounded-lg')}
        >
          <StagePipelinePanel compId={editComp.id} modes={currentModes} defaultSeriesFormat={currentSeries} />
        </div>
      )}

      {/* Clan Standings (M3, comp-lifecycle-pipeline) — a sibling of the
          pipeline above, not its own wizard step: it's comp-level "what the
          pipeline produces, summed" (every team's results rolled back up to
          its clan), so it belongs in the same scroll context where the
          admin is already looking at individual stage results. */}
      {isEdit && <ClanRollupPanel compId={editComp.id} />}

      <ErrorMsg msg={error} />
      <div className="flex gap-2">
        <button
          onClick={() => mut.mutate(form)}
          disabled={!form.name.trim() || mut.isPending || currentModes.length === 0 || (!isEdit && stages.length === 0) || signUpWindowInverted}
          className="btn-primary flex-1"
        >
          {isEdit ? (mut.isPending ? 'Saving…' : 'Save changes') : (mut.isPending ? 'Creating…' : 'Create')}
        </button>
        <button onClick={() => { setError(''); if (onCancel) onCancel(); else setOpen(false); }} className="btn-ghost">Cancel</button>
      </div>

      {/* Danger zone — quiet, bottom of the panel, edit mode only. onDone
          already closes this panel + invalidates ['competitions'] (see
          TournamentSetup.tsx's editingComp wiring), so a successful delete
          reuses it instead of duplicating that plumbing. */}
      {isEdit && (
        <DeleteCompetitionButton
          compId={editComp.id}
          name={editComp.name}
          isLive={editComp.status === 'LIVE'}
          onDeleted={onDone}
        />
      )}
    </div>
  );
}
